import { getAddressSuggestions } from '@uplift/api';
import { AddressSuggestionsInput, AddressSuggestionType, AddressType } from '@uplift/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddressVerifyModalTypes } from './AddressVerifyModal.component';

export type FetchAddressVerifyHookTypes = {
  addressVerify: AddressType;
  constraint: boolean;
  onAddressConfirm: AddressVerifyModalTypes['onAddressConfirm'];
};

export const useFetchAddressVerifySuggestion = ({ addressVerify, constraint, onAddressConfirm }: FetchAddressVerifyHookTypes) => {
  const [ suggestion, setSuggestion ] = useState<AddressSuggestionType | null>(null);
  const [ loading, setLoading ] = useState(true);

  const fetchAddressSuggestions = useCallback((text) => {
    const data: AddressSuggestionsInput = { text };

    return getAddressSuggestions(data);
  }, []);

  const addressMatch = useCallback((addressVerify: AddressType, addressSuggestion: AddressType) => {
    const keys: (keyof AddressType)[] = [
      'address1',
      //   'address2',
      'city',
      'state',
      'zip' ];

    return !keys.some(key =>addressVerify[key] !== (addressSuggestion[key] ?? ''));
  }, []);

  const suggestionParser = useCallback((addressSuggestion: AddressType, addressVerify: AddressType): AddressType => {
    if (addressVerify.address2) {
      return { ...addressSuggestion, address2: addressVerify.address2 };
    } else {
      return { ...addressSuggestion, address2: null };
    }
  }, []);

  const text = useMemo(() => {
    const { address1, city, state, zip } = addressVerify;
    const values = [ address1, city, state, zip ];

    return values.filter(x => x).join(' ');
  }, [ addressVerify ]);

  useEffect(() => {
    if (!constraint) {
      setSuggestion(null);
      setLoading(true);
      return;
    };
    setLoading(true);
    fetchAddressSuggestions(text).then((addressSuggestions) => {
      const firstSuggestion = addressSuggestions?.[0] ?? null;

      if (firstSuggestion && addressMatch(addressVerify, firstSuggestion['address'])) {
        onAddressConfirm(addressVerify);
        return;
      }

      const parsedAddressSuggestion = firstSuggestion
        ? { ...firstSuggestion, address: suggestionParser(firstSuggestion.address, addressVerify) }
        : null;

      setSuggestion(parsedAddressSuggestion);
      setLoading(false);
    }).catch(() => {
      setSuggestion(null);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ constraint, text, fetchAddressSuggestions, addressMatch ]);

  return { suggestion, loading };
};